const statNames = ['str', 'int', 'wil', 'dex', 'con'];

//Roll set of 20 stats
export function rerollStats(homeland, profession, currentStatsum) {
  const results = [];

  for (let count = 0; count < 20; count++) {
    let bestStats = null;
    
    for (let retry = 0; retry < 50; retry++) {
      let rolledStats = rollStats(homeland, profession);

      if (!bestStats || rolledStats.statsum > bestStats.statsum) {
        bestStats = rolledStats;
        if (bestStats.statsum >= currentStatsum) {
          break;
        }
      }
    }

    results.push(bestStats);
  }

  return results;
}

//Roll single stats
export function rollStats(homeland, profession) {
  const isTrolloc = homeland.race === 'Trolloc';
  const isBearish = isTrolloc && homeland.name === 'Bearish';
  const minStatsum = isTrolloc ? 65 : 70;
  const maxStr = isBearish ? 21 : 19;
  const maxCon = isBearish ? 17 : 19;

  const results = { str: 0, int: 0, wil: 0, dex: 0, con: 0, statsum: 0 };
  for (let retry = 0; retry < 10; retry++) {
    //Roll the stats
    for (let statName of statNames) {
      const rolledD100 = Math.floor(Math.random() * 100) + 1;
      const d100Modifier = 4 - homeland.d100Cumulative.findIndex(value => rolledD100 <= value);
      let rolledStat = homeland.baseStats[statName] + profession.statModifiers[statName] + d100Modifier;

      switch (statName) {
        case 'str':
          rolledStat = Math.min(rolledStat, maxStr);
          break;
        case 'con':
          rolledStat = Math.min(rolledStat, maxCon);
          break;
        default:
          rolledStat = Math.min(rolledStat, 19);
          break;
      }

      results[statName] = rolledStat;
    }

    //Reduce if str, dex, & con are all 19
    if (results.str >= 19 && results.dex >= 19 && results.con >= 19) {
      const highStats = ['str', 'dex', 'con'];
      const randomStatName = highStats[Math.floor(Math.random() * highStats.length)];
      results[randomStatName] = 18;
    }

    //break if statsum >= 65 (Trolloc) / 70 (Non-Trolloc)
    results.statsum = results.str + results.int + results.wil + results.dex + results.con;
    if (results.statsum >= minStatsum) {
      break;
    }
  }

  return results;
}

//get max stats for a homeland + class
export function getMaxStats(homeland, profession) {
  const isTrolloc = homeland.race === 'Trolloc';
  const isBearish = isTrolloc && homeland.name === 'Bearish';

  const results = { str: 0, int: 0, wil: 0, dex: 0, con: 0, statsum: 0 };
  for (let statName of statNames) {
    let roll = homeland.baseStats[statName] + profession.statModifiers[statName] + 4;
    roll = Math.min(roll, (isBearish && statName === 'str' ? 21 : (isBearish && statName === 'con' ? 17 : 19)));
    results[statName] = roll;
  }

  results.statsum = results.str + results.int + results.wil + results.dex + results.con;
  return results;
}

//get min stats for a homeland + class
export function getMinStats(homeland, profession) {
  const isTrolloc = homeland.race === 'Trolloc';
  const isBearish = isTrolloc && homeland.name === 'Bearish';

  const results = { str: 0, int: 0, wil: 0, dex: 0, con: 0, statsum: 0 };
  for (let statName of statNames) {
    let roll = homeland.baseStats[statName] + profession.statModifiers[statName] - 4;
    roll = Math.min(roll, (isBearish && statName === 'str' ? 21 : (isBearish && statName === 'con' ? 17 : 19)));
    results[statName] = roll;
  }

  results.statsum = results.str + results.int + results.wil + results.dex + results.con;
  return results;
}

export function getDifficulty(homeland, profession) {
  const d100Chances = homeland.d100Chances;
  const statModifiers = profession.statModifiers;

  const homelandScore = Math.min(d100Chances[0], 10) / 10;
  const professionScore = .7 + .1 * Math.min(statModifiers.str + statModifiers.int + statModifiers.wil + statModifiers.dex + statModifiers.con, 3);
  const score = homelandScore * professionScore;
  
  let label;
  if (score <= .1) {
    label = 'Insane';
  } else if (score <= .2) {
    label = 'Very Hard';
  } else if (score <= .4) {
    label = 'Hard';
  } else if (score <= .6) {
    label = 'Medium';
  } else if (score <= .8) {
    label = 'Easy';
  } else {
    label = 'Very Easy';
  }

  return label;
}

export function estimateProbability(homeland, profession, currentStatsum, targetStats) {
  const probMeetsTarget    = rawProbability(homeland, profession, targetStats);
  const probReachesStatsum = rawProbability(homeland, profession, { targetStatsum: currentStatsum });

  let probMissesEveryAttempt = 0;
  let probNotYetStopped      = 1;

  for (let slot = 1; slot <= 50; slot++) {
    const probRollsAtThisSlot  = slot < 50 ? probNotYetStopped * probReachesStatsum : probNotYetStopped;
    probMissesEveryAttempt    += probRollsAtThisSlot * Math.pow(1 - probMeetsTarget, slot);
    probNotYetStopped         *= (1 - probReachesStatsum);
  }

  return Math.max(0, Math.min(1, (1 - probMissesEveryAttempt) * 20));
}

export function rawProbability(homeland, profession, targetStats) {
  const isTrolloc  = homeland.race === 'Trolloc';
  const isBearish  = isTrolloc && homeland.name === 'Bearish';
  const minStatsum = isTrolloc ? 65 : 70;
  const maxStr     = isBearish ? 21 : 19;
  const maxCon     = isBearish ? 17 : 19;

  // Use integer counts to avoid floating point accumulation entirely.
  // Scaled by 3 to handle the 1/3 reduction weight without fractions:
  //   - Non-reduction outcome  → weight = 3
  //   - Each reduction outcome → weight = 1  (3 outcomes × 1 = 3, same total)
  // Max possible value 3×100^5 = 3×10^10, well within JS safe integer range (~9×10^15).
  let weightedSuccess    = 0;
  let weightedSuccessAny = 0;
  let weightedInvalid    = 0;
  let weightedTotal      = 0;

  for (let strIdx = 0; strIdx < 9; strIdx++) {
    const rolledStr = Math.min(homeland.baseStats.str + profession.statModifiers.str + (4 - strIdx), maxStr);
    const chanceStr = homeland.d100Chances[strIdx];

    for (let intIdx = 0; intIdx < 9; intIdx++) {
      const rolledInt = Math.min(homeland.baseStats.int + profession.statModifiers.int + (4 - intIdx), 19);
      const chanceInt = homeland.d100Chances[intIdx];

      for (let wilIdx = 0; wilIdx < 9; wilIdx++) {
        const rolledWil = Math.min(homeland.baseStats.wil + profession.statModifiers.wil + (4 - wilIdx), 19);
        const chanceWil = homeland.d100Chances[wilIdx];

        for (let dexIdx = 0; dexIdx < 9; dexIdx++) {
          const rolledDex = Math.min(homeland.baseStats.dex + profession.statModifiers.dex + (4 - dexIdx), 19);
          const chanceDex = homeland.d100Chances[dexIdx];

          for (let conIdx = 0; conIdx < 9; conIdx++) {
            const rolledCon = Math.min(homeland.baseStats.con + profession.statModifiers.con + (4 - conIdx), maxCon);
            const chanceCon = homeland.d100Chances[conIdx];

            const chanceOfRoll = chanceStr * chanceInt * chanceWil * chanceDex * chanceCon;

            const outcomes = rolledStr >= 19 && rolledDex >= 19 && rolledCon >= 19
              ? [ { str: 18, dex: rolledDex, con: rolledCon, weight: 1 }, { str: rolledStr, dex: 18, con: rolledCon, weight: 1 }, { str: rolledStr, dex: rolledDex, con: 18, weight: 1 } ]
              : [ { str: rolledStr, dex: rolledDex, con: rolledCon, weight: 3 } ];

            for (const outcome of outcomes) {
              const scaledChance = chanceOfRoll * outcome.weight;
              const statSum = outcome.str + rolledInt + rolledWil + outcome.dex + outcome.con;

              weightedTotal += scaledChance;

              const meetsTargets =
                (!targetStats.str           || outcome.str >= targetStats.str) &&
                (!targetStats.int           || rolledInt   >= targetStats.int) &&
                (!targetStats.wil           || rolledWil   >= targetStats.wil) &&
                (!targetStats.dex           || outcome.dex >= targetStats.dex) &&
                (!targetStats.con           || outcome.con >= targetStats.con) &&
                (!targetStats.targetStatsum || statSum     >= targetStats.targetStatsum);

              if (statSum < minStatsum) {
                weightedInvalid += scaledChance;
                if (meetsTargets) weightedSuccessAny += scaledChance;
              } else {
                if (meetsTargets) {
                  weightedSuccess    += scaledChance;
                  weightedSuccessAny += scaledChance;
                }
              }
            }
          }
        }
      }
    }
  }

  // Single division per accumulator at the end — no floating point error inside the loops.
  // Dividing by weightedTotal also self-corrects if d100Chances doesn't sum to exactly 100.
  const probSuccess    = weightedSuccess    / weightedTotal;
  const probSuccessAny = weightedSuccessAny / weightedTotal;
  const probInvalid    = weightedInvalid    / weightedTotal;

  // Expand geometric series explicitly to stay numerically stable when probInvalid ≈ 1.
  // probInvalidToNinth ends as probInvalid^9; the 10th attempt keeps the roll
  // regardless of minStatsum, so it uses probSuccessAny rather than probSuccess.
  let sumOfInvalidPowers = 0;
  let probInvalidToNinth = 1;
  for (let attempt = 0; attempt < 9; attempt++) {
    sumOfInvalidPowers += probInvalidToNinth;
    probInvalidToNinth *= probInvalid;
  }

  return Math.max(0, Math.min(1, probSuccess * sumOfInvalidPowers + probInvalidToNinth * probSuccessAny));
}

export function formatChance(chance) {
  if (chance === 0) return '0';

  const percent = chance * 100;

  if (percent < 0.00001) return 'near 0';

  let digits;
  if (percent < 0.0001) digits = 6;     //.000095->.000096
  else if (percent < 0.001) digits = 5; //.00095->.00096
  else if (percent < 0.01) digits = 4;  //.0095->.0096
  else if (percent < 0.1) digits = 3;   //.095->.096
  else if (percent < 1) digits = 2;     //.95->.96
  else if (percent < 10) digits = 1;    //9.5->9.6
  else digits = 0;                      //11.5 -> 12

  return percent.toLocaleString(undefined, { maximumFractionDigits: digits });
}

export function formatOdds(chance) {
  if (chance === 0) return '∞';

  const odds = 1 / chance;

  return odds.toLocaleString(undefined, { maximumFractionDigits: 0 });
}